import React from 'react';

const PricingPage = () => {
  return (
    <div className="bg-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16">
          <h1 className="text-6xl font-bold mb-4 text-gray-900">Pricing</h1>
          <p className="text-xl text-gray-600 mb-2">
            Choose the right plan for you and your team. Don't know what you need? 
            <a href="/contact" className="text-purple-600 hover:text-purple-500 ml-1">Get in touch.</a>
          </p>
        </div>

        {/* Pricing cards in a more compact row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Starter Plan */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col h-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-3 text-gray-900">Starter</h2>
              <p className="text-sm text-gray-600 mb-6">Up to 100 datasets</p>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-gray-900">$2,500</span>
                <span className="text-gray-500 ml-1">/month</span>
              </div>
              
              <a href="/contact" className="block w-full py-2.5 px-4 text-center border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-6">
                Contact Us
              </a>
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-col flex-grow">
              <h4 className="font-semibold text-gray-700 mb-5">Features include:</h4>
              <ul className="space-y-3 mb-auto">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Basic anomaly detection</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Standard dashboards</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Data quality monitoring</span>
                </li>
              </ul>
              
              <div className="mt-6 pt-5 border-t border-gray-200 h-[120px] flex flex-col justify-center">
                <h4 className="font-semibold text-gray-900 mb-2">Support:</h4>
                <p className="text-gray-600">Standard email support</p>
              </div>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col h-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-3 text-gray-900">Pro</h2>
              <p className="text-sm text-gray-600 mb-6">Up to 500 datasets</p>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-gray-900">$5,500</span>
                <span className="text-gray-500 ml-1">/month</span>
              </div>
              
              <a href="/contact" className="block w-full py-2.5 px-4 text-center bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors mb-6">
                Contact Us
              </a>
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-col flex-grow">
              <h4 className="font-semibold text-gray-700 mb-4">Everything in <span className="text-gray-500">Starter</span>, plus:</h4>
              <ul className="space-y-3 mb-auto">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Advanced ML-driven detection</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">BI/ETL integrations</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Enhanced analytics</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Root-cause analysis</span>
                </li>
              </ul>
              
              <div className="mt-6 pt-5 border-t border-gray-200 h-[120px] flex flex-col justify-center">
                <h4 className="font-semibold text-gray-900 mb-2">Support:</h4>
                <p className="text-gray-600">Priority support (faster SLAs)</p>
              </div>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col h-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-3 text-gray-900">Enterprise</h2>
              <p className="text-sm text-gray-600 mb-6">Up to 1000 datasets</p>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-gray-900">$12,500</span>
                <span className="text-gray-500 ml-1">/month</span>
              </div>
              
              <a href="/contact" className="block w-full py-2.5 px-4 text-center bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors mb-6">
                Contact Us
              </a>
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-col flex-grow">
              <h4 className="font-semibold text-gray-700 mb-4">Everything in <span className="text-gray-500">Pro</span>, plus:</h4>
              <ul className="space-y-3 mb-auto">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Full feature set</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Advanced ML detection</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Automated remediation suggestions</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Custom integrations</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Enterprise security & compliance</span>
                </li>
              </ul>
              
              <div className="mt-6 pt-5 border-t border-gray-200 h-[120px] flex flex-col justify-center">
                <h4 className="font-semibold text-gray-900 mb-2">Support:</h4>
                <p className="text-gray-600 line-clamp-3">Premium 24/7 support, dedicated account manager, professional services included</p>
              </div>
            </div>
          </div>

          {/* Enterprise Plus Plan */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col h-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-3 text-gray-900">Enterprise +</h2>
              <p className="text-sm text-gray-600 mb-6">unlimited datasets</p>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-gray-900">Custom</span>
              </div>
              
              <a href="/contact" className="block w-full py-2.5 px-4 text-center border border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white rounded-lg transition-colors mb-6">
                Contact us
              </a>
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-col flex-grow">
              <h4 className="font-semibold text-gray-700 mb-4">Everything in <span className="text-gray-500">Enterprise</span>, plus:</h4>
              <ul className="space-y-3 mb-auto">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">All Enterprise features</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Tailored enhancements</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Custom deployments</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-gray-600">Unlimited scalability</span>
                </li>
              </ul>
              
              <div className="mt-6 pt-5 border-t border-gray-200 h-[120px] flex flex-col justify-center">
                <h4 className="font-semibold text-gray-900 mb-2">Support:</h4>
                <p className="text-gray-600 line-clamp-3">Premium 24/7 support, dedicated team, custom SLAs</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Compare all features section */}
        <div className="mt-16">
          {/* <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Compare plan features</h2> */}
          
          {/* This section could be expanded with a detailed feature comparison table */}
          <div className="text-center">
            <a href="/contact" className="inline-block py-3 px-6 text-center bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors mt-4">
              Get in touch to learn more
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage; 